import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 외부 링크(`/v1/external-links`) — <b>메뉴가 바깥으로 나가는 주소</b> (사용자 지시 2026-09-01).
 *
 * 사이드바(`useMenuTree`) · 라우트(`screens.tsx`) · 설정 화면(`ExternalApiKeysPage`)이 <b>같은 값</b>을
 * 본다. 그래서 훅을 `pages/settings` 가 아니라 `lib` 에 둔다 — 사이드바가 설정 화면 폴더를 향해
 * import 하면 그 화면을 옮기는 날 사이드바가 함께 깨진다(`useShortcuts` 와 같은 이유).
 *
 * 조회는 로그인한 모든 사용자에게 열려 있고(그러지 않으면 메뉴가 어디로 갈지 모른다),
 * 저장만 `settings_external_api` 편집 권한을 요구한다.
 *
 * **값이 오기 전에는 화면 상수를 쓴다.** 프론트에도 같은 기본값이 박혀 있어(`menuConfig`)
 * 서버 응답을 기다리는 동안에도 링크가 비지 않는다 — 서버 값이 오면 그쪽이 이긴다.
 */

export type ExternalLinkStatus = {
  /** `leave_plan`. 저장 경로와 `MENU_EXTERNAL_LINKS` 의 값이 같은 이름을 쓴다. */
  code: string;
  label: string;
  /** 이 주소를 누가 언제 보는가. 잘못 넣었을 때 무엇이 깨지는지가 여기 있다. */
  description: string;
  /** `sys_app_settings` 의 키(`external.link.leave-plan`). 표를 직접 봐야 하는 날을 위해 화면에 세운다. */
  settingKey: string;
  /** 지금 쓰이는 주소. 화면 값이 있으면 그것, 없으면 `defaultUrl`. */
  url: string;
  /** 화면 값을 비웠을 때 되돌아갈 주소. */
  defaultUrl: string;
  /** 화면에서 넣은 값으로 도는 중인가. */
  custom: boolean;
};

export type ExternalLinksView = { links: ExternalLinkStatus[] };

export function useExternalLinks() {
  return useQuery({
    queryKey: queryKeys.externalLinks,
    queryFn: () => apiFetch<ExternalLinksView>('/v1/external-links'),
    // 몇 달에 한 번 바뀌는 값이다. 화면을 옮길 때마다 사이드바가 다시 물으면 요청만 는다.
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * 코드 → 지금 주소. 아직 응답이 없으면 빈 객체이고, 호출부가 화면 상수로 물러선다.
 *
 * **`useMemo` 로 같은 객체를 돌려준다.** 사이드바가 이 값을 의존성으로 들고 있어서, 그릴 때마다 새
 * 객체를 주면 메뉴 트리 전체가 매번 다시 계산된다.
 */
export function useExternalLinkUrls(): Record<string, string> {
  const { data } = useExternalLinks();
  return useMemo(
    () => Object.fromEntries((data?.links ?? []).map((link) => [link.code, link.url])),
    [data],
  );
}

export function useSaveExternalLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, url }: { code: string; url: string }) =>
      apiFetch<ExternalLinksView>(`/v1/settings/external-api/links/${code}`, { method: 'PUT', body: { url } }),
    // 응답이 곧 새 상태다. 사이드바가 같은 캐시를 보므로 저장 즉시 메뉴가 새 주소를 가리킨다.
    onSuccess: (view) => queryClient.setQueryData(queryKeys.externalLinks, view),
  });
}
